import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Button, Table, Badge, Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import TableCard from '../../components/common/TableCard';
import ConfirmModal from '../../components/common/ConfirmModal';
import AddItemStatusModal from '../../components/Configs/AddItemStatusModal';
import EditItemStatusModal from '../../components/Configs/EditItemStatusModal';
import { getStatuses, updateStatus } from '../../services/Other/GeneralQueries';

function ItemStatusList() {
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState([]);

    const [showAddStatusModal, setShowAddStatusModal] = useState(false);
    const [showEditStatusModal, setShowEditStatusModal] = useState(false);
    const [selectedItemStatusId, setSelectedItemStatusId] = useState(0);

    // filtros
    const [statusFilter, setStatusFilter] = useState('active'); // default activos
    const [search, setSearch] = useState('');
    const [pageSize, setPageSize] = useState(10);

    // paginación
    const [page, setPage] = useState(1);

    // confirm eliminar
    const [confirm, setConfirm] = useState({
        show: false,
        id: null,
        name: '',
        isLoading: false,
    });

    const fetchRows = async () => {
        setLoading(true);
        try {
            const list = await getStatuses();
            setStatus(Array.isArray(list) ? list : []);
        } catch (e) {
            console.error(e);
            toast.error('No se pudieron cargar los estatus.');
            setStatus([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRows();
    }, []);

    useEffect(() => {
        setPage(1);
    }, [statusFilter, search, pageSize]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();

        return status
            .filter((r) => {
                const active = !!r.isActive;
                if (statusFilter === 'active') return active;
                if (statusFilter === 'inactive') return !active;
                return true;
            })
            .filter((r) => {
                if (!q) return true;
                return String(r.name ?? '').toLowerCase().includes(q);
            });
    }, [status, statusFilter, search]);

    const pageCount = Math.ceil(filtered.length / pageSize);
    const safePageCount = pageCount; // puede ser 0
    const currentPage = safePageCount === 0 ? 0 : Math.min(page, safePageCount);

    const totalRows = filtered.length;

    const getVisiblePages = (currentPage, totalPages, maxButtons = 5) => {
        let start = Math.max(1, currentPage - 2);
        let end = Math.min(totalPages, start + maxButtons - 1);

        if (end - start < maxButtons - 1) {
            start = Math.max(1, end - maxButtons + 1);
        }

        const pages = [];
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    };

    const visiblePages = getVisiblePages(page, pageCount);

    const pagedRows = useMemo(() => {
        if (safePageCount === 0) return [];
        const start = (currentPage - 1) * pageSize;
        return filtered.slice(start, start + pageSize);
    }, [filtered, currentPage, pageSize, safePageCount]);

    const handleAddStatus = () => setShowAddStatusModal(true);

    const handleOpenEditModal = (id) => {
        setSelectedItemStatusId(id);
        setShowEditStatusModal(true);
    };

    const askDelete = (item) =>
        setConfirm({ show: true, id: item.id, name: item.name, isLoading: false });

    const confirmDelete = async () => {
        if (!confirm.id) return;

        setConfirm((p) => ({ ...p, isLoading: true }));
        try {
            const res = await updateStatus(confirm.id, confirm.name, false);
            if (!res?.result && res !== true) {
                toast.error('No se pudo eliminar el estatus.');
                return;
            }

            toast.success('Estatus eliminado.');
            setConfirm({ show: false, id: null, name: '', isLoading: false });
            fetchRows();
        } catch (e) {
            console.error(e);
            toast.error('Error eliminando estatus.');
        } finally {
            setConfirm((p) => ({ ...p, isLoading: false }));
        }
    };

    return (
        <>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-3">
                <h2 className="mb-0">Estado de artículos</h2>

                <Button variant="primary" onClick={handleAddStatus}>
                    Agregar
                </Button>
            </div>

            {/* Filtros */}
            <Row className="g-3 align-items-end mb-3">
                <Col md={3}>
                    <Form.Label>Estado</Form.Label>
                    <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="active">Activos</option>
                        <option value="inactive">Inactivos</option>
                        <option value="all">Todos</option>
                    </Form.Select>
                </Col>

                <Col md={6}>
                    <Form.Label>Buscar</Form.Label>
                    <Form.Control
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar por nombre..."
                    />
                </Col>

                <Col md={3}>
                    <Form.Label>Por página</Form.Label>
                    <Form.Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                        {[10, 20, 30, 50].map((n) => (
                            <option key={n} value={n}>{n}</option>
                        ))}
                    </Form.Select>
                </Col>
            </Row>

            {/* Tabla */}
            <TableCard>
                <Table bordered hover responsive className="mb-0 align-middle">
                    <thead>
                        <tr>
                            <th className="actions-col">Acciones</th>
                            <th>Nombre</th>
                            <th>Estatus</th>
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={3} className="text-center py-5">
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Cargando...
                                </td>
                            </tr>
                        ) : pagedRows.length === 0 ? (
                            <tr>
                                <td colSpan={3} className="text-center py-5">Sin registros</td>
                            </tr>
                        ) : (
                            pagedRows.map((item) => (
                                <tr key={item.id}>
                                    <td className="actions-col">
                                        <div className="actions-cell">
                                            <Button variant="outline-primary" size="sm" onClick={() => handleOpenEditModal(item.id)}>
                                                Editar
                                            </Button>
                                            <Button variant="outline-danger" size="sm" onClick={() => askDelete(item)}>
                                                Eliminar
                                            </Button>
                                        </div>
                                    </td>

                                    <td>{item.name}</td>

                                    <td>
                                        {item.isActive ? <Badge bg="success">Activa</Badge> : <Badge bg="secondary">Inactiva</Badge>}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </TableCard>

            {/* Paginación */}
            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                <div className="text-muted">
                    Página {currentPage} de {safePageCount} ({totalRows} registros)
                </div>

                <div className="d-flex gap-2 align-items-center flex-wrap">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setPage(1)}
                        disabled={safePageCount === 0 || currentPage <= 1}
                    >
                        «
                    </Button>

                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={safePageCount === 0 || currentPage <= 1}
                    >
                        Anterior
                    </Button>

                    {visiblePages.length > 0 && visiblePages[0] > 1 && (
                        <>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setPage(1)}
                            >
                                1
                            </Button>
                            {visiblePages[0] > 2 && <span className="px-1 text-muted">…</span>}
                        </>
                    )}

                    {visiblePages.map((pageNumber) => (
                        <Button
                            key={pageNumber}
                            size="sm"
                            variant={pageNumber === currentPage ? 'secondary' : 'outline-secondary'}
                            onClick={() => setPage(pageNumber)}
                        >
                            {pageNumber}
                        </Button>
                    ))}

                    {visiblePages.length > 0 && visiblePages[visiblePages.length - 1] < safePageCount && (
                        <>
                            {visiblePages[visiblePages.length - 1] < safePageCount - 1 && (
                                <span className="px-1 text-muted">…</span>
                            )}
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setPage(safePageCount)}
                            >
                                {safePageCount}
                            </Button>
                        </>
                    )}

                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setPage((p) => Math.min(safePageCount, p + 1))}
                        disabled={safePageCount === 0 || currentPage >= safePageCount}
                    >
                        Siguiente
                    </Button>

                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setPage(safePageCount)}
                        disabled={safePageCount === 0 || currentPage >= safePageCount}
                    >
                        »
                    </Button>
                </div>
            </div>

            {/* Modales */}
            <AddItemStatusModal
                show={showAddStatusModal}
                handleClose={() => setShowAddStatusModal(false)}
                handleReload={fetchRows}
            />

            <EditItemStatusModal
                statusId={selectedItemStatusId}
                show={showEditStatusModal}
                handleClose={() => setShowEditStatusModal(false)}
                handleReload={fetchRows}
            />

            {/* Confirm eliminar */}
            <ConfirmModal
                show={confirm.show}
                onHide={() => setConfirm({ show: false, id: null, name: '', isLoading: false })}
                onConfirm={confirmDelete}
                title="Eliminar estatus"
                message={`¿Seguro que deseas eliminar "${confirm.name}"?`}
                confirmText="Eliminar"
                confirmVariant="danger"
                denyText={null}
                isLoading={confirm.isLoading}
            />
        </>
    );
}

export default ItemStatusList;